import { z } from "zod";

/**
 * Every permission primitive in the system, shared so the backend enforces
 * and the frontend gates against the same constants (no raw strings).
 */
export const Permission = {
  SETTINGS_EDIT: "settings.edit",
  USERS_VIEW: "users.view",
  USERS_EDIT: "users.edit",
  AGENTPULSE_VIEW: "agentpulse.view",
  ANALYTICS_VIEW: "analytics.view",
  INVOICES_VIEW: "invoices.view",
  INVOICES_EDIT: "invoices.edit",
  ITEMS_VIEW: "items.view",
  ITEMS_EDIT: "items.edit",
  OPERATIONS_VIEW: "operations.view",
  OPERATIONS_EDIT: "operations.edit",
  LOCATIONS_VIEW: "locations.view",
  LOCATIONS_EDIT: "locations.edit",
  DEMANDFORECAST_VIEW: "demandforecast.view",
  DEMANDFORECAST_EDIT: "demandforecast.edit",
  VAULT_VIEW: "vault.view",
  VAULT_EDIT: "vault.edit",
  PRICELISTS_VIEW: "pricelists.view",
  PRICELISTS_EDIT: "pricelists.edit",
  SHOPRULES_VIEW: "shoprules.view",
  SHOPRULES_EDIT: "shoprules.edit",
  QUOTES_VIEW_OWN: "quotes.view.own",
  QUOTES_VIEW_ALL: "quotes.view.all",
  QUOTES_EDIT_OWN: "quotes.edit.own",
  QUOTES_EDIT_ALL: "quotes.edit.all",
  OPPORTUNITIES_VIEW_OWN: "opportunities.view.own",
  OPPORTUNITIES_VIEW_ALL: "opportunities.view.all",
  OPPORTUNITIES_EDIT_OWN: "opportunities.edit.own",
  OPPORTUNITIES_EDIT_ALL: "opportunities.edit.all",
  COMPANIES_VIEW_OWN: "companies.view.own",
  COMPANIES_VIEW_ALL: "companies.view.all",
  COMPANIES_EDIT_OWN: "companies.edit.own",
  COMPANIES_EDIT_ALL: "companies.edit.all",
  PEOPLE_VIEW_OWN: "people.view.own",
  PEOPLE_VIEW_ALL: "people.view.all",
  PEOPLE_EDIT_OWN: "people.edit.own",
  PEOPLE_EDIT_ALL: "people.edit.all",
  TASKS_VIEW_ALL: "tasks.view.all",
  TASKS_MANAGE: "tasks.manage",
  SALESORDERS_VIEW: "salesorders.view",
  SALESORDERS_EDIT: "salesorders.edit",
  DATASETS_VIEW_ALL: "datasets.view.all",
  ONBOARDING_VIEW: "onboarding.view",
  ONBOARDING_INTERACT: "onboarding.interact",
  SETTINGS_ONBOARDING_EDIT: "settings.onboarding.edit",
} as const;

/** A single permission string value, e.g. "settings.edit". */
export type Permission = (typeof Permission)[keyof typeof Permission];

/** Zod schema validating a permission string against the known primitives. */
export const PermissionSchema = z.enum(Permission);

/**
 * Whether a user may edit an owner-scoped entity: allowed with the org-wide
 * edit permission, or with the owner-scoped edit permission when they own
 * the entity. Ownership is passed as a precomputed boolean so both
 * single-owner entities (companies, quotes) and multi-owner entities
 * (opportunities) can use it.
 * @param canEditAll - Whether the user holds the org-wide edit permission
 * @param canEditOwn - Whether the user holds the owner-scoped edit permission
 * @param userOwnsEntity - Whether the user owns the entity
 * @returns True when the user may edit the entity
 */
export const canEditOwnedEntity = (
  canEditAll: boolean,
  canEditOwn: boolean,
  userOwnsEntity: boolean,
): boolean => canEditAll || (canEditOwn && userOwnsEntity);

/**
 * The ownership fields used to decide whether a principal owns an opportunity:
 * the sales rep and the estimators.
 */
export type OpportunityOwnership = {
  salesRepUserId?: string | null;
  estimatorUserIds?: string[] | null;
};

/**
 * Whether the user owns the opportunity: they are its sales rep or one of its
 * estimators.
 * @param userId - The acting user id, or undefined for a service principal
 * @param opportunity - The opportunity's ownership fields
 * @returns True when the user owns the opportunity
 */
export function isOpportunityOwner(
  userId: string | undefined,
  opportunity: OpportunityOwnership,
): boolean {
  if (!userId) {
    return false;
  }
  return (
    opportunity.salesRepUserId === userId ||
    (opportunity.estimatorUserIds ?? []).includes(userId)
  );
}
